import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useT } from "../../i18n/LangContext";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const input = "mt-1.5 w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base text-black outline-none placeholder:text-zinc-500 focus:border-black";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-zinc-700">{label}</span>
    {children}
  </label>
);

const RoleToggle = ({ f, value, onChange }) => (
  <div className="mt-1.5 grid grid-cols-2 gap-2" role="radiogroup" aria-label={f.role}>
    {["passenger", "driver"].map((r) => (
      <button
        key={r}
        type="button"
        role="radio"
        aria-checked={value === r}
        onClick={() => onChange(r)}
        className={`rounded-lg border px-4 py-3 text-base font-medium transition-colors ${value === r ? "border-black bg-black text-white" : "border-black/15 bg-white text-black hover:border-black/40"}`}
        data-testid={`delete-role-${r}`}
      >
        {f[r]}
      </button>
    ))}
  </div>
);

export const DeleteForm = () => {
  const t = useT();
  const f = t.legal.deletion.form;
  const [v, setV] = useState({ name: "", phone: "", email: "", role: "passenger", reason: "" });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!v.name.trim() || !v.phone.trim() || !EMAIL_RE.test(v.email.trim())) {
      setState("invalid");
      return;
    }
    setState("sending");
    try {
      const res = await fetch(`${API}/api/account-deletion-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...v, reason: v.reason.trim() || null, lang: t.lang }),
      });
      setState(res.ok ? "done" : "error");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div className="mt-10 flex gap-3 rounded-xl bg-[#F3F3F3] p-6" role="status" data-testid="delete-success">
        <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-[#FFD000]" />
        <p className="text-base text-black">{f.success}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mt-10 space-y-5 rounded-xl bg-[#F3F3F3] p-6 sm:p-8" data-testid="delete-form">
      <h2 className="text-xl font-bold text-black lg:text-2xl">{f.title}</h2>
      <Field label={f.name}>
        <input value={v.name} onChange={set("name")} autoComplete="name" className={input} data-testid="delete-name-input" />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={f.phone}>
          <input type="tel" value={v.phone} onChange={set("phone")} autoComplete="tel" className={input} data-testid="delete-phone-input" />
        </Field>
        <Field label={f.email}>
          <input type="email" value={v.email} onChange={set("email")} autoComplete="email" className={input} data-testid="delete-email-input" />
        </Field>
      </div>
      <div>
        <span className="text-sm font-medium text-zinc-700">{f.role}</span>
        <RoleToggle f={f} value={v.role} onChange={(r) => setV((p) => ({ ...p, role: r }))} />
      </div>
      <Field label={f.reason}>
        <textarea value={v.reason} onChange={set("reason")} rows={3} maxLength={1000} className={input} data-testid="delete-reason-input" />
      </Field>
      {(state === "invalid" || state === "error") && (
        <p className="text-sm font-medium text-red-600" role="alert" data-testid="delete-error">
          {state === "invalid" ? f.required : f.error}
        </p>
      )}
      <button
        type="submit"
        disabled={state === "sending"}
        className="w-full rounded-lg bg-black px-6 py-3.5 text-base font-medium text-white transition-colors hover:bg-zinc-800 disabled:opacity-60 sm:w-auto"
        data-testid="delete-submit-btn"
      >
        {state === "sending" ? f.sending : f.submit}
      </button>
    </form>
  );
};
